import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Calendar,
    ShoppingCart,
    ChefHat,
    Snowflake,
    AlertCircle,
    Clock,
    DollarSign,
    CheckCircle,
    ArrowRight,
    Loader2,
    ChevronLeft,
    Users,
    Printer,
    History,
    Download
} from 'lucide-react';

export const PackDetailPage = () => {
    const { packId } = useParams();
    const { user, isPremium } = useAuth();
    const navigate = useNavigate();
    const [pack, setPack] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSection, setActiveSection] = useState('soupers');

    useEffect(() => {
        loadPack();
    }, [packId]);

    const loadPack = async () => {
        try {
            setLoading(true);
            const response = await api.getWeeklyPackById(packId);
            setPack(response.data);
        } catch (error) {
            console.error('Error loading pack:', error);
            if (error.response?.status === 403) {
                setError('premium');
            } else if (error.response?.status === 404) {
                setError('notfound');
            } else {
                setError('error');
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="font-medium text-muted-foreground">Chargement du pack...</p>
                </div>
            </div>
        );
    }

    if (error === 'premium') {
        return (
            <div className="min-h-screen py-12">
                <div className="max-w-2xl mx-auto px-4 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="font-heading text-3xl font-bold mb-4">Accès Premium requis</h1>
                    <p className="text-muted-foreground mb-8">
                        Abonnez-vous pour accéder à l'historique complet des packs.
                    </p>
                    <Button onClick={() => navigate('/subscription')} className="bg-primary">
                        Devenir Premium
                    </Button>
                </div>
            </div>
        );
    }

    if (error === 'notfound' || !pack) {
        return (
            <div className="min-h-screen py-12">
                <div className="max-w-2xl mx-auto px-4 text-center">
                    <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                        <Calendar className="w-10 h-10 text-muted-foreground" />
                    </div>
                    <h1 className="font-heading text-3xl font-bold mb-4">Pack non trouvé</h1>
                    <p className="text-muted-foreground mb-8">
                        Ce pack n'existe pas ou a été supprimé.
                    </p>
                    <Button onClick={() => navigate('/packs/history')} variant="outline">
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Retour à l'historique
                    </Button>
                </div>
            </div>
        );
    }

    const sections = [
        { id: 'soupers', label: 'Soupers', icon: ChefHat },
        { id: 'epicerie', label: 'Épicerie', icon: ShoppingCart },
        { id: 'conseils', label: 'Conseils', icon: Snowflake },
    ];

    const formatDate = (dateStr) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('fr-CA', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
            });
        } catch {
            return '';
        }
    };

    return (
        <div className="min-h-screen py-8 md:py-12">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/packs/history')}
                        className="mb-4"
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Historique des packs
                    </Button>
                    
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-3xl p-6 md:p-8 text-white">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                            <div className="flex items-center gap-2">
                                <Badge className="bg-white/20 text-white border-0">
                                    <History className="w-3 h-3 mr-1" />
                                    Archive
                                </Badge>
                                <Badge className="bg-white/20 text-white border-0 font-bold">
                                    Pack #{pack.pack_number || pack.week_number}
                                </Badge>
                            </div>
                            <button 
                                onClick={async () => {
                                    try {
                                        await api.downloadPackPdf(pack.id);
                                    } catch (error) {
                                        console.error('Error downloading PDF:', error);
                                    }
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur rounded-full font-bold text-sm transition-colors"
                            >
                                <Download className="w-4 h-4" />
                                Télécharger PDF
                            </button>
                        </div>
                        <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
                            {pack.title}
                        </h1>
                        <p className="text-white/80 mb-4">{pack.description}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span>{formatDate(pack.created_at)}</span>
                            </div>
                            {pack.total_cost > 0 && (
                                <div className="flex items-center gap-2">
                                    <DollarSign className="w-4 h-4" />
                                    <span>Budget: {pack.total_cost.toFixed(2)}$</span>
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <ChefHat className="w-4 h-4" />
                                <span>{pack.soupers?.length || 0} soupers</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                    {sections.map(section => (
                        <Button
                            key={section.id}
                            variant={activeSection === section.id ? "default" : "outline"}
                            onClick={() => setActiveSection(section.id)}
                            className="rounded-full whitespace-nowrap"
                        >
                            <section.icon className="w-4 h-4 mr-2" />
                            {section.label}
                        </Button>
                    ))}
                </div>

                {/* Content Sections */}
                {activeSection === 'soupers' && (
                    <div className="space-y-4">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <ChefHat className="w-6 h-6 text-primary" />
                            Les 5 soupers de la semaine
                        </h2>
                        <div className="grid gap-4">
                            {pack.soupers?.map((souper, index) => (
                                <SouperCard key={index} souper={souper} index={index} />
                            ))}
                        </div>
                    </div>
                )}

                {activeSection === 'epicerie' && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                                <ShoppingCart className="w-6 h-6 text-green-500" />
                                Liste d'épicerie
                            </h2>
                            <Button variant="outline" size="sm" onClick={() => window.print()}>
                                <Printer className="w-4 h-4 mr-2" />
                                Imprimer
                            </Button>
                        </div>
                        <Card className="rounded-2xl">
                            <CardContent className="p-6">
                                {pack.grocery_list?.length > 0 ? (
                                    <ul className="space-y-2">
                                        {pack.grocery_list.map((item, i) => (
                                            <li key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
                                                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                                <span className="flex-1">{item.name || item}</span>
                                                {item.quantity && (
                                                    <span className="text-muted-foreground">{item.quantity}</span>
                                                )}
                                                {item.estimated_cost && (
                                                    <span className="font-medium text-green-600">
                                                        {item.estimated_cost}$
                                                    </span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-muted-foreground text-center py-8">
                                        Aucune liste d'épicerie disponible pour ce pack.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}

                {activeSection === 'conseils' && (
                    <div className="space-y-6">
                        {/* Freezing Tips */}
                        {pack.freezing_tips?.length > 0 && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold flex items-center gap-2 mb-4">
                                    <Snowflake className="w-6 h-6 text-cyan-500" />
                                    Astuces congélation
                                </h2>
                                <Card className="rounded-2xl">
                                    <CardContent className="p-6">
                                        <ul className="space-y-3">
                                            {pack.freezing_tips.map((tip, i) => (
                                                <li key={i} className="flex items-start gap-3">
                                                    <Snowflake className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                                                    <span>{tip}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Emergency Recipe */}
                        {pack.emergency_recipe && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold flex items-center gap-2 mb-4">
                                    <AlertCircle className="w-6 h-6 text-orange-500" />
                                    Recette secours
                                </h2>
                                <Card className="rounded-2xl border-orange-200 bg-orange-50">
                                    <CardContent className="p-6">
                                        <h3 className="font-bold text-lg mb-2">
                                            {pack.emergency_recipe.title}
                                        </h3>
                                        <p className="text-muted-foreground">
                                            {pack.emergency_recipe.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* How to use */}
                        {pack.how_to_use?.length > 0 && (
                            <div>
                                <h2 className="font-heading text-2xl font-bold mb-4">
                                    Comment utiliser ce pack
                                </h2>
                                <Card className="rounded-2xl">
                                    <CardContent className="p-6">
                                        <ol className="space-y-3 list-decimal list-inside">
                                            {pack.how_to_use.map((step, i) => (
                                                <li key={i} className="text-muted-foreground">
                                                    {step}
                                                </li>
                                            ))}
                                        </ol>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const SouperCard = ({ souper, index }) => {
    const navigate = useNavigate();
    const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi'];
    const dayColors = [
        'bg-green-500',
        'bg-blue-500',
        'bg-purple-500',
        'bg-orange-500',
        'bg-pink-500'
    ];

    const recipe = souper.recipe;

    return (
        <Card className="rounded-2xl overflow-hidden hover:shadow-lg transition-shadow">
            <div className="flex flex-col md:flex-row">
                {/* Day indicator */}
                <div className={`${dayColors[index]} p-4 md:w-32 flex md:flex-col items-center justify-center text-white`}>
                    <span className="font-heading font-bold text-lg">{days[index]}</span>
                </div>

                {/* Content */}
                <CardContent className="flex-1 p-4 md:p-6">
                    <div className="flex gap-4">
                        {/* Image */}
                        {(souper.image_url || recipe?.image_url) && (
                            <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden flex-shrink-0 bg-muted">
                                <img 
                                    src={souper.image_url || recipe?.image_url} 
                                    alt={souper.title}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold text-lg mb-2 line-clamp-2">
                                {souper.title}
                            </h3>

                            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-3">
                                {(souper.prep_time || recipe?.prep_time) && (
                                    <div className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        <span>{(souper.prep_time || 0) + (souper.cook_time || recipe?.cook_time || 0)} min</span>
                                    </div>
                                )}
                                {(souper.cost_per_serving || recipe?.cost_per_serving) && (
                                    <div className="flex items-center gap-1 text-green-600">
                                        <DollarSign className="w-4 h-4" />
                                        <span>{(souper.cost_per_serving || recipe?.cost_per_serving)?.toFixed(2)}$/portion</span>
                                    </div>
                                )}
                                {recipe?.servings && (
                                    <div className="flex items-center gap-1">
                                        <Users className="w-4 h-4" />
                                        <span>{recipe.servings} portions</span>
                                    </div>
                                )}
                            </div>

                            {/* View recipe button */}
                            {recipe?.id && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate(`/recipes/${recipe.id}`)}
                                    className="rounded-full"
                                >
                                    Voir la recette
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            )}
                        </div>
                    </div>
                </CardContent>
            </div>
        </Card>
    );
};

export default PackDetailPage;
